import { apiClient } from '@/core/infra_api_client'
import type { PaginatedResponse, CloudProvider } from '@/core/infra_types'

// ============= Types =============

export interface CloudCredential {
  id: string
  organizationId: string
  name: string
  cloudProvider: CloudProvider
  credentialType: string
  vaultPath: string
  status: 'active' | 'rotating' | 'expired'
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
  createdBy: string
  lastValidatedAt?: string
}

export interface CreateCredentialRequest {
  name: string
  cloudProvider: CloudProvider
  credentialType: string
  secretData: Record<string, string>
  metadata?: Record<string, unknown>
}

export interface ValidateResult {
  valid: boolean
  message: string
}

// ============= Credential types per provider =============

export const credentialTypesByProvider: Record<CloudProvider, { value: string; label: string }[]> = {
  gcp: [{ value: 'service_account', label: 'Service Account JSON' }],
  azure: [{ value: 'service_principal', label: 'Service Principal' }],
  aws: [{ value: 'iam_role', label: 'IAM Role' }],
  upcloud: [{ value: 'api_token', label: 'API Token' }],
  none: [],
}

// ============= API =============

const BASE_PATH = 'credentials'

export async function listCredentials(params?: {
  page?: number
  perPage?: number
  cloudProvider?: string
  status?: string
}): Promise<PaginatedResponse<CloudCredential>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.cloudProvider) searchParams['cloud_provider'] = params.cloudProvider
  if (params?.status) searchParams['status'] = params.status

  return apiClient.get<PaginatedResponse<CloudCredential>>(BASE_PATH, searchParams)
}

export async function getCredential(id: string): Promise<CloudCredential> {
  return apiClient.get<CloudCredential>(`${BASE_PATH}/${id}`)
}

export async function createCredential(data: CreateCredentialRequest): Promise<CloudCredential> {
  return apiClient.post<CloudCredential, CreateCredentialRequest>(BASE_PATH, data)
}

export async function deleteCredential(id: string): Promise<void> {
  return apiClient.delete(`${BASE_PATH}/${id}`)
}

export async function validateCredential(id: string): Promise<ValidateResult> {
  return apiClient.post<ValidateResult, undefined>(`${BASE_PATH}/${id}/validate`, undefined)
}
